import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';

import { apiClient } from '../config/api-client';
import { queryKeys } from '../config/query-keys';
import { DataTable, type Column } from '../components/data-table';

interface ArticleRow {
  readonly id: string;
  readonly title: string;
  readonly url: string;
  readonly language: string;
  readonly status: string;
  readonly isAiProcessed: boolean;
  readonly isDuplicate: boolean;
  readonly summary: string | null;
  readonly channelId: string;
  readonly channelName: string | null;
  readonly publishedAt: string | null;
  readonly createdAt: string;
}

interface Response {
  readonly rows: readonly ArticleRow[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

const PAGE_SIZE = 50;

function statusChip(status: string, processed: boolean, duplicate: boolean) {
  if (duplicate) return <span className="nw-chip nw-chip--warning">duplicate</span>;
  if (status === 'failed') return <span className="nw-chip nw-chip--error">{status}</span>;
  if (status === 'pending') return <span className="nw-chip nw-chip--warning">{status}</span>;
  if (processed) return <span className="nw-chip nw-chip--success">{status}</span>;
  return <span className="nw-chip">{status}</span>;
}

const columns: readonly Column<ArticleRow>[] = [
  {
    key: 'title',
    header: 'Title',
    render: (r) => (
      <a
        href={r.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'var(--text-primary)',
          fontWeight: 500,
          textDecoration: 'none',
        }}
        title={r.url}
      >
        {r.title}
      </a>
    ),
  },
  {
    key: 'channelName',
    header: 'Channel',
    width: '180px',
    render: (r) =>
      r.channelName ? (
        <span style={{ color: 'var(--text-secondary)' }}>{r.channelName}</span>
      ) : (
        '—'
      ),
  },
  {
    key: 'language',
    header: 'Lang',
    width: '70px',
    render: (r) => <span className="nw-chip">{r.language}</span>,
  },
  {
    key: 'status',
    header: 'Status',
    width: '130px',
    render: (r) => statusChip(r.status, r.isAiProcessed, r.isDuplicate),
  },
  {
    key: 'publishedAt',
    header: 'Published',
    width: '160px',
    render: (r) =>
      r.publishedAt ? (
        <span title={r.publishedAt} style={{ color: 'var(--text-secondary)' }}>
          {formatDistanceToNow(new Date(r.publishedAt), { addSuffix: true })}
        </span>
      ) : (
        '—'
      ),
  },
];

export function ContentArticles() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [language, setLanguage] = useState('');
  const [q, setQ] = useState('');

  const params = { page, pageSize: PAGE_SIZE, status, language, q };
  const query = useQuery({
    queryKey: queryKeys.content.articles(params),
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('page', String(page));
      search.set('pageSize', String(PAGE_SIZE));
      if (status) search.set('status', status);
      if (language) search.set('language', language);
      if (q) search.set('q', q);
      const res = await apiClient.get<Response>(
        `/owner/content/articles?${search.toString()}`,
      );
      return res.data;
    },
    placeholderData: (prev) => prev,
  });

  const data = query.data;

  return (
    <>
      <header className="nw-page-header">
        <h1 className="nw-page-header__title">Content</h1>
        <p className="nw-page-header__subtitle">
          Every article ingested by RSS workers, with AI-processing status and source channel.
        </p>
      </header>

      <div className="nw-filters">
        <input
          className="nw-filters__input"
          placeholder="Search by title…"
          value={q}
          onChange={(e) => {
            setPage(1);
            setQ(e.target.value);
          }}
        />
        <select
          className="nw-filters__input"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="processed">Processed</option>
          <option value="failed">Failed</option>
        </select>
        <input
          className="nw-filters__input"
          placeholder="Lang (e.g. en)"
          value={language}
          maxLength={3}
          onChange={(e) => {
            setPage(1);
            setLanguage(e.target.value);
          }}
        />
      </div>

      <DataTable
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => r.id}
        total={data?.total}
        page={data?.page ?? page}
        pageSize={data?.pageSize ?? PAGE_SIZE}
        onPageChange={setPage}
        loading={query.isLoading || query.isFetching}
        error={query.error ? (query.error as Error).message : null}
        emptyMessage="No articles match these filters."
      />
    </>
  );
}
